const onDocumentReady = callback => {
    if (
        document.readyState === 'complete' ||
        document.readyState === 'interactive'
    ) {
        callback();
        return;
    }

    document.addEventListener('DOMContentLoaded', callback, { once: true });
};

const initPromoUnderMenu = () => {
    const promoBar = document.querySelector('.promo-under-menu');

    if (!promoBar) {
        return;
    }

    const header = document.querySelector('.header');
    const body = document.body;
    let placeholder = promoBar.nextElementSibling;
    const closeButton = promoBar.querySelector('.promo-under-menu__close');
    const tabToggle = promoBar.querySelector('.promo-under-menu__tab');
    const STORAGE_KEY = 'promoUnderMenuCollapsed';
    const MOBILE_BREAKPOINT = '(max-width: 991px)';
    const mobileMediaQuery =
        typeof window.matchMedia === 'function'
            ? window.matchMedia(MOBILE_BREAKPOINT)
            : null;

    if (
        !placeholder ||
        !placeholder.classList.contains('promo-under-menu__placeholder')
    ) {
        placeholder = document.createElement('div');
        placeholder.classList.add('promo-under-menu__placeholder');
        promoBar.parentNode.insertBefore(placeholder, promoBar.nextSibling);
    }

    const collapsedStorage = {
        get() {
            try {
                return window.sessionStorage.getItem(STORAGE_KEY) === '1';
            } catch (error) {
                return false;
            }
        },
        set() {
            try {
                window.sessionStorage.setItem(STORAGE_KEY, '1');
            } catch (error) {
                // storage not available, ignore
            }
        },
        clear() {
            try {
                window.sessionStorage.removeItem(STORAGE_KEY);
            } catch (error) {
                // storage not available, ignore
            }
        },
    };

    const isMobileView = () => {
        if (!mobileMediaQuery) {
            return window.innerWidth <= 991;
        }

        return mobileMediaQuery.matches;
    };

    const syncTabAria = isCollapsed => {
        if (!tabToggle) {
            return;
        }

        tabToggle.setAttribute('aria-expanded', (!isCollapsed).toString());
    };

    let stickyStart = 0;
    let scrollTicking = false;
    let resizeTicking = false;
    let lastTranslateValue = null;
    let headerTransitionFrame = null;

    const setOffsetVariable = value => {
        promoBar.style.setProperty('--promo-under-menu-offset', `${value}px`);
    };

    const syncPlaceholder = isActive => {
        placeholder.style.display = isActive ? 'block' : 'none';
        placeholder.style.height = isActive
            ? `${promoBar.offsetHeight}px`
            : '0px';
    };

    const getHeaderOffset = () => {
        if (!header) {
            return 0;
        }

        const headerStyles = window.getComputedStyle(header);
        const isFixed = headerStyles.position === 'fixed';

        if (!isFixed) {
            return 0;
        }

        return header.getBoundingClientRect().height;
    };

    const getHeaderVisibleOffset = () => {
        if (!header) {
            return 0;
        }

        const headerStyles = window.getComputedStyle(header);
        if (headerStyles.position !== 'fixed') {
            return 0;
        }

        const rect = header.getBoundingClientRect();
        if (rect.bottom <= 0) {
            return 0;
        }

        return Math.max(0, Math.min(rect.bottom, rect.height));
    };

    const getBodyPaddingTop = () => {
        if (!body) {
            return 0;
        }

        const paddingValue = parseFloat(
            window.getComputedStyle(body).paddingTop
        );
        return Number.isNaN(paddingValue) ? 0 : paddingValue;
    };

    const updateStickyState = (suppressAnimation = false) => {
        const headerOffset = getHeaderOffset();
        const mobilePadding = headerOffset > 0 ? getBodyPaddingTop() : 0;
        const effectiveOffset = Math.max(headerOffset - mobilePadding, 0);
        setOffsetVariable(effectiveOffset);

        const shouldStick = window.scrollY >= stickyStart;
        promoBar.classList.toggle('is-sticky', shouldStick);
        syncPlaceholder(shouldStick);

        if (!shouldStick) {
            promoBar.style.removeProperty('--promo-under-menu-translate');
            lastTranslateValue = null;
            return;
        }

        const translateValue = getHeaderVisibleOffset();

        if (lastTranslateValue !== translateValue) {
            if (suppressAnimation) {
                promoBar.classList.add('promo-under-menu--no-transition');
                window.requestAnimationFrame(() => {
                    promoBar.classList.remove(
                        'promo-under-menu--no-transition'
                    );
                });
            }

            promoBar.style.setProperty(
                '--promo-under-menu-translate',
                `${translateValue}px`
            );
            lastTranslateValue = translateValue;
        }
    };

    const stopHeaderTransitionSync = () => {
        if (headerTransitionFrame === null) {
            return;
        }

        window.cancelAnimationFrame(headerTransitionFrame);
        headerTransitionFrame = null;
        promoBar.classList.remove('promo-under-menu--header-sync');
    };

    const syncDuringHeaderTransition = () => {
        stopHeaderTransitionSync();
        promoBar.classList.add('promo-under-menu--header-sync');

        const syncFrame = () => {
            updateStickyState();
            headerTransitionFrame = window.requestAnimationFrame(syncFrame);
        };

        headerTransitionFrame = window.requestAnimationFrame(syncFrame);
    };

    const updateMeasurements = () => {
        promoBar.classList.remove('is-sticky');
        syncPlaceholder(false);

        const { top } = promoBar.getBoundingClientRect();
        stickyStart = top + window.scrollY;

        updateStickyState(true);
    };

    const handleScroll = () => {
        if (scrollTicking) {
            return;
        }

        scrollTicking = true;
        window.requestAnimationFrame(() => {
            scrollTicking = false;
            updateStickyState();
        });
    };

    const handleResize = () => {
        if (resizeTicking) {
            return;
        }

        resizeTicking = true;
        window.requestAnimationFrame(() => {
            resizeTicking = false;
            updateMeasurements();
            if (!mobileMediaQuery) {
                handleMobileChange();
            }
        });
    };

    const setCollapsedState = (shouldCollapse, { silent = false } = {}) => {
        const collapse = Boolean(shouldCollapse) && isMobileView();

        promoBar.classList.toggle('promo-under-menu--collapsed', collapse);
        syncTabAria(collapse);

        if (!silent) {
            if (collapse) {
                collapsedStorage.set();
            } else {
                collapsedStorage.clear();
            }
        }

        window.requestAnimationFrame(() => {
            updateMeasurements();
        });
    };

    const handleMobileChange = () => {
        if (!isMobileView()) {
            setCollapsedState(false, { silent: true });
            return;
        }

        const shouldCollapse = collapsedStorage.get();
        setCollapsedState(shouldCollapse, { silent: true });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    if (header) {
        header.addEventListener('transitionstart', event => {
            if (event.propertyName !== 'transform') {
                return;
            }

            syncDuringHeaderTransition();
        });

        const handleHeaderTransitionEnd = event => {
            if (event.propertyName !== 'transform') {
                return;
            }

            stopHeaderTransitionSync();
            updateStickyState();
        };

        header.addEventListener('transitionend', handleHeaderTransitionEnd);
        header.addEventListener('transitioncancel', handleHeaderTransitionEnd);
    }

    if (closeButton) {
        closeButton.addEventListener('click', () => {
            setCollapsedState(true);
        });
    }

    if (tabToggle) {
        tabToggle.addEventListener('click', () => {
            setCollapsedState(false);
        });
    }

    if (mobileMediaQuery) {
        if (typeof mobileMediaQuery.addEventListener === 'function') {
            mobileMediaQuery.addEventListener('change', handleMobileChange);
        } else if (typeof mobileMediaQuery.addListener === 'function') {
            mobileMediaQuery.addListener(handleMobileChange);
        }
    }

    handleMobileChange();

    updateMeasurements();
};

onDocumentReady(initPromoUnderMenu);
